import type { DisciplinaryAssessment, VerificationCheck } from "./types";

type Evidence = VerificationCheck["evidenceManifest"][number];
const OPMC_MATCHING_DATA_KEY = /^opmc-\d+-data$/;
const OPMC_SUMMARY_KEY = /^opmc-\d+-summary$/;

export function disciplinaryEvidenceKeys(assessment: DisciplinaryAssessment) {
  return new Set([
    ...assessment.evidenceKeys,
    ...assessment.records.flatMap((record) => [
      ...record.evidenceKeys,
      ...record.orders.flatMap((order) =>
        order.evidenceKey ? [order.evidenceKey] : [],
      ),
    ]),
  ]);
}

/** Associate retained evidence once, keeping native orders beside their public record. */
export function groupDisciplinaryEvidence({
  assessment,
  evidence,
}: {
  assessment: DisciplinaryAssessment;
  evidence: Evidence[];
}) {
  const keys = disciplinaryEvidenceKeys(assessment);
  const available = evidence.filter((file) => keys.has(file.key));
  const used = new Set<string>();
  const records = assessment.records.map((record) => {
    const orders = record.orders.map((order) => {
      const file = available.find(
        (file) =>
          file.key === order.evidenceKey &&
          file.mediaType === "application/pdf",
      );
      const alreadyShown = Boolean(file && used.has(file.fileUploadId));
      if (file) {
        used.add(file.fileUploadId);
      }
      return { order, evidence: file, alreadyShown };
    });
    const captures = available.filter((file) => {
      if (
        !record.evidenceKeys.includes(file.key) ||
        used.has(file.fileUploadId)
      ) {
        return false;
      }
      used.add(file.fileUploadId);
      return true;
    });
    return { record, orders, captures };
  });
  const sourceEvidence = available.filter(
    (file) => !used.has(file.fileUploadId),
  );
  return { records, sourceEvidence };
}

export function isPublicBoardEvidence(evidence: Evidence) {
  return (
    evidence.kind === "disciplinary-search" ||
    evidence.kind === "disciplinary-record" ||
    evidence.kind === "board-order"
  );
}

export function disciplinaryEvidenceLabel(evidence: Evidence, index: number) {
  const number = index + 1;
  if (evidence.kind === "board-order") {
    return evidence.mediaType === "application/pdf"
      ? `Board order ${number} (PDF)`
      : `Board order evidence ${number}`;
  }
  if (evidence.kind === "disciplinary-record") {
    return evidence.mediaType === "image/png"
      ? `Board record screenshot ${number}`
      : `Board record evidence ${number}`;
  }
  if (evidence.kind === "disciplinary-search") {
    return evidence.mediaType === "image/png"
      ? `Board search screenshot ${number}`
      : `Board search evidence ${number}`;
  }
  if (evidence.mediaType === "application/json") {
    if (OPMC_MATCHING_DATA_KEY.test(evidence.key)) {
      return "OPMC matching data";
    }
    if (OPMC_SUMMARY_KEY.test(evidence.key)) {
      return "OPMC screening summary";
    }
    return `Board source data ${number}`;
  }
  return `Board supporting evidence ${number}`;
}
